import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Loader } from '@els/els-react--loader';
import { commaFormatNumber } from '@Utils/String';
import RsSlidable, { CONTROLS_PLACEMENTS } from '@Pages/RetroSynthesis/components/RsSlidable/RsSlidable';

import './DatabaseSwitcherTabs.scss';

// classes for Leyden styles, just to keep them in one place
export const C_ELS_CLASSES = {
  tabGroup: 'c-els-tab-group',
  tabGroupItem: 'c-els-tab-group__item',
  tabGroupItemActive: 'c-els-tab-group__item--active',
  tabGroupItemDisabled: 'c-els-tab-group__item--disabled',
  tabGroupText: 'c-els-tab-group__text',
};

function DatabaseSwitcherTabGroup({ className, currentDatabase, isDbSwitcherVisible, availableDatabases, selectDatabase }) {
  const tabsRenderer = (item) => (
    <button
      disabled={!item.count}
      type="button"
      className={cn(C_ELS_CLASSES.tabGroupText)}
      onClick={() => {
        selectDatabase(item);
      }}>
      <span className="database-switcher-new__tab-content">
        {item.title}
        {/* eslint-disable-next-line no-nested-ternary */}
        {item.isLoading ? (
          <Loader isSmall isBlocking={false} className="database-switcher-new__tab-loader" />
        ) : item.canceled ? (
          'canceled'
        ) : (
          commaFormatNumber(item.count)
        )}
      </span>
    </button>
  );

  const currentTabIndex = useMemo(
    () => currentDatabase && availableDatabases.findIndex(({ databaseId }) => databaseId === currentDatabase.databaseId),
    [availableDatabases, currentDatabase]
  );

  const contentAttrs = {
    role: 'tablist',
    'aria-label': 'tabs',
  };
  const itemAttrs = ({ databaseId, context }) => ({
    id: `databases-tab-${context}-${databaseId}`,
    role: 'tab',
    'aria-selected': databaseId === currentDatabase.databaseId,
    'aria-controls': `results-tabpanel-${context}-${databaseId}`,
  });

  return isDbSwitcherVisible ? (
    <div className={cn('database-switcher-new', 'u-els-padding-top-1o2', className)}>
      <RsSlidable
        className="database-switcher-new__slidable rx-flex-1"
        containerClassName="database-switcher-new__list-container"
        contentClassName={cn(C_ELS_CLASSES.tabGroup)}
        contentAttrs={contentAttrs}
        itemClassName={({ databaseId, count }) =>
          cn(C_ELS_CLASSES.tabGroupItem, {
            [C_ELS_CLASSES.tabGroupItemActive]: databaseId === currentDatabase.databaseId,
            [C_ELS_CLASSES.tabGroupItemDisabled]: !count,
          })
        }
        itemAttrs={itemAttrs}
        items={availableDatabases}
        renderItem={tabsRenderer}
        visibleItemIndex={currentTabIndex}
        controlsPlacement={CONTROLS_PLACEMENTS.bothSides}
      />
    </div>
  ) : null;
}

DatabaseSwitcherTabGroup.propTypes = {
  className: PropTypes.string,
  currentDatabase: PropTypes.object,
  isDbSwitcherVisible: PropTypes.bool,
  selectDatabase: PropTypes.func,
  availableDatabases: PropTypes.array,
};

export default DatabaseSwitcherTabGroup;
